import { FilePdfOutlined, LoadingOutlined } from '@ant-design/icons'
import { Document, Page, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import { Button, Drawer, Spin } from 'antd'
import { BaseButtonProps } from 'antd/lib/button/button'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { usePriceEstimatePdfQuery } from '../../../graphql/generated'
import { monitPdfCss } from '../../utils/monitPdfCss'
import { useMapImage } from '../../utils/useMapImage'
import { useToggleState } from '../../utils/useToggleState'
import { PriceEstimatePdf } from '../PriceEstimatePdf/PriceEstimatePdf'

export interface PriceEstimateSelectBoxPdfButtonProps {
  buttonProps?: BaseButtonProps
  buttonText?: React.ReactNode
  id: string
}

const loader = <LoadingOutlined style={{ fontSize: 34, color: 'white' }} spin />

const bodyStyle = {
  body: {
    display: 'flex',
    padding: 0,
    position: 'relative' as const,
  },
}

export const PriceEstimateSelectBoxPdfButton: FunctionComponent<PriceEstimateSelectBoxPdfButtonProps> =
  props => {
    const [isDrawerOpen, toggleDrawerOpen] = useToggleState()
    const [loading, setLoading] = useState(false)

    const [pdfDataResponse, refetch] = usePriceEstimatePdfQuery({
      variables: { id: props.id },
      pause: true,
    })

    const imgSrc = useMapImage(
      pdfDataResponse.data?.monitById?.lat,
      pdfDataResponse.data?.monitById?.lon,
      pdfDataResponse.data?.monitById?.propertyType,
      pdfDataResponse.data?.monitById?.polygon
    )

    const handleOnClick = useCallback(() => {
      setLoading(true)
      toggleDrawerOpen()
      if (
        !pdfDataResponse.fetching &&
        props.id !== pdfDataResponse?.data?.monitById?.id
      )
        refetch()

      setTimeout(() => setLoading(false), 2000)
    }, [
      pdfDataResponse?.data?.monitById?.id,
      pdfDataResponse.fetching,
      props.id,
      refetch,
      toggleDrawerOpen,
    ])

    const sanitizedFileName = useMemo(
      () =>
        pdfDataResponse.data?.monitById?.title
          ? pdfDataResponse.data.monitById.title
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-zA-Z0-9-]/g, '_')
              .replace(/__|_-_/g, '_')
              .slice(0, 45) + '.pdf'
          : 'REGESTA DATA - Cenový odhad.pdf',
      [pdfDataResponse?.data?.monitById?.title]
    )

    const pdfContent = useMemo(() => {
      if (!!pdfDataResponse.data?.monitById && imgSrc.map && imgSrc.streetView)
        return (
          <PriceEstimatePdf
            imgSrc={imgSrc}
            data={pdfDataResponse.data?.monitById}
          />
        )

      return (
        <Document>
          <Page size="A4" style={monitPdfCss.page} />
        </Document>
      )
    }, [pdfDataResponse.data?.monitById, imgSrc])

    const extra = useMemo(
      () => (
        <div className="space-x-2">
          <Button onClick={toggleDrawerOpen}>Zavřít</Button>
          <PDFDownloadLink document={pdfContent} fileName={sanitizedFileName}>
            <Button type="primary" onClick={toggleDrawerOpen}>
              Stáhnout PDF
            </Button>
          </PDFDownloadLink>
        </div>
      ),
      [toggleDrawerOpen, pdfContent, sanitizedFileName]
    )

    return (
      <>
        <Button
          title="Zobrazit PDF cenového odhadu"
          type="primary"
          onClick={handleOnClick}
          icon={<FilePdfOutlined />}
          loading={loading || pdfDataResponse.fetching}
          disabled={!!pdfDataResponse.error}
          ghost
          className={
            (props.id ? 'opacity-100 max-w-xs mr-2' : 'opacity-0 max-w-0') +
            ' transition-all'
          }
          {...props.buttonProps}
        >
          {props.buttonText}
        </Button>

        <Drawer
          title="Náhled cenového odhadu"
          placement="right"
          size="large"
          onClose={toggleDrawerOpen}
          open={isDrawerOpen}
          styles={bodyStyle}
          extra={extra}
          destroyOnClose
        >
          <div className="flex flex-col w-full h-full bg-gray-400 animate-pulse absolute items-center justify-center">
            <Spin indicator={loader} />
          </div>
          <PDFViewer className="z-10" width="100%">
            {pdfContent}
          </PDFViewer>
        </Drawer>
      </>
    )
  }
